
import AdminLayout from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { User, Image, Printer, FileText, BookOpen, Globe } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getPreviewUrl } from "@/utils/imageUtils";
import { Link } from "react-router-dom";

export default function Index() {
  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => {
      const [creatorsCount, artworksCount, printsCount, certificatesCount, editionsCount, webhooksCount] = await Promise.all([
        supabase.from("creators").select("*", { count: "exact", head: true }),
        supabase.from("artworks").select("*", { count: "exact", head: true }),
        supabase.from("prints").select("*", { count: "exact", head: true }),
        supabase.from("certificates").select("*", { count: "exact", head: true }),
        supabase.from("editions").select("*", { count: "exact", head: true }),
        supabase.from("webhook_triggers").select("*", { count: "exact", head: true }),
      ]);

      return {
        creators: creatorsCount.count || 0,
        artworks: artworksCount.count || 0,
        prints: printsCount.count || 0,
        certificates: certificatesCount.count || 0,
        editions: editionsCount.count || 0,
        webhooks: webhooksCount.count || 0,
      };
    },
  });

  // Fetch recent artworks
  const { data: recentArtworks } = useQuery({
    queryKey: ["recent-artworks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("artworks")
        .select(`
          *,
          creators (
            name,
            surname
          )
        `)
        .order("stamp", { ascending: false })
        .limit(4);

      if (error) throw error;
      return data;
    },
  });

  // Fetch recent prints
  const { data: recentPrints } = useQuery({
    queryKey: ["recent-prints"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("prints")
        .select(`
          *,
          editions (
            edition_name,
            artworks (
              title
            )
          )
        `)
        .order("print_date", { ascending: false })
        .limit(5);

      if (error) throw error;
      return data;
    },
  });

  // Fetch recent webhook activity
  const { data: webhookActivity } = useQuery({
    queryKey: ["webhook-activity"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("webhook_delivery_logs")
        .select(`
          log_id,
          attempt_timestamp,
          response_status,
          webhook_queue (
            webhook_triggers (
              webhook_event_types (
                event_code
              )
            )
          )
        `)
        .order("attempt_timestamp", { ascending: false })
        .limit(5);

      if (error) throw error;
      return data;
    },
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-left">Dashboard Overview</h1>

        {/* Stats Overview */}
        <div className="grid grid-cols-6 gap-4">
          <Card>
            <CardContent className="flex items-center justify-between p-6">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Creators</p>
                <p className="text-3xl font-bold">{stats?.creators || 0}</p>
              </div>
              <User className="h-8 w-8 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center justify-between p-6">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Registered Artworks</p>
                <p className="text-3xl font-bold">{stats?.artworks || 0}</p>
              </div>
              <Image className="h-8 w-8 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center justify-between p-6">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Available Editions</p>
                <p className="text-3xl font-bold">{stats?.editions || 0}</p>
              </div>
              <BookOpen className="h-8 w-8 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center justify-between p-6">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Ordered Prints</p>
                <p className="text-3xl font-bold">{stats?.prints || 0}</p>
              </div>
              <Printer className="h-8 w-8 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center justify-between p-6">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Certificates Issued</p>
                <p className="text-3xl font-bold">{stats?.certificates || 0}</p>
              </div>
              <FileText className="h-8 w-8 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center justify-between p-6">
              <Link to="/webhooks" className="w-full">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Webhook Triggers</p>
                    <p className="text-3xl font-bold">{stats?.webhooks || 0}</p>
                  </div>
                  <Globe className="h-8 w-8 text-muted-foreground" />
                </div>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Recent Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Artworks */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-bold mb-4">Recent Artworks</h2>
              <div className="grid grid-cols-2 gap-4">
                {recentArtworks?.map((artwork) => (
                  <div key={artwork.artwork_id} className="space-y-2">
                    <img
                      src={getPreviewUrl(artwork.preview)}
                      alt={artwork.title}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <h3 className="font-medium">{artwork.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {artwork.creators?.name} {artwork.creators?.surname}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Prints */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-bold mb-4">Recent Prints</h2>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Artwork</TableHead>
                    <TableHead>Edition</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentPrints?.map((print) => (
                    <TableRow key={print.print_id}>
                      <TableCell>{print.editions?.artworks?.title}</TableCell>
                      <TableCell>{print.editions?.edition_name}</TableCell>
                      <TableCell className="capitalize">{print.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Webhook Activity */}
        {webhookActivity && webhookActivity.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Recent Webhook Activity</h2>
                <Link to="/webhooks" className="text-blue-500 hover:underline text-sm">
                  View All
                </Link>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event Type</TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {webhookActivity.map((log) => (
                    <TableRow key={log.log_id}>
                      <TableCell>
                        {log.webhook_queue?.webhook_triggers?.webhook_event_types?.event_code || 'Unknown'}
                      </TableCell>
                      <TableCell>
                        {new Date(log.attempt_timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {log.response_status >= 200 && log.response_status < 300 ? (
                          <span className="text-green-500">Success</span>
                        ) : (
                          <span className="text-red-500">Failed</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
